import { useMemo } from 'react';
import { useShallow } from 'zustand/react/shallow';
import { useStore, selectAsignaturasActivas } from '../store/useStore';
import { useTerminologia } from '../hooks/useTerminologia';
import { HelpIcon } from '../components/ui/HelpIcon';
import { NivelCalificacion } from '../logic/types';
import {
  calcularCreditosAcumulados,
  calcularPromedioGeneral,
  calcularPromedioAprobaciones,
  calcularPromedioPonderadoCreditos,
  calcularFrecuenciaConceptos,
  calcularCreditosPorModulo,
  DEFAULT_ESCALA_FCEA
} from '../logic/calculos';
import { Calculator, BarChart3 } from 'lucide-react';

export function ResumenTab() {
  const {
    asignaturas,
    evaluaciones,
    escalas,
    historialAcademico,
    activeFacultadId,
    activeCarreraId,
    carreras,
    facultades,
    modulos,
    acreditacionesLibres,
    isLoading
  } = useStore(
    useShallow(s => ({
      asignaturas: s.asignaturas,
      evaluaciones: s.evaluaciones,
      escalas: s.escalas,
      historialAcademico: s.historialAcademico,
      activeFacultadId: s.activeFacultadId,
      activeCarreraId: s.activeCarreraId,
      carreras: s.carreras,
      facultades: s.facultades,
      modulos: s.modulos,
      acreditacionesLibres: s.acreditacionesLibres,
      isLoading: s.isLoading,
    }))
  );
  const { materias, siglaFacultad } = useTerminologia();

  const facultadActiva = facultades.find(f => f.id === activeFacultadId);
  const escalaVigente = escalas.find(e => e.facultad === facultadActiva?.nombre || e.facultad === siglaFacultad) || escalas[0] || DEFAULT_ESCALA_FCEA;

  const activeAsignaturas = useMemo(() => selectAsignaturasActivas(asignaturas), [asignaturas]);

  const creditosTotales = calcularCreditosAcumulados(activeAsignaturas);
  const carreraActiva = carreras.find(c => c.id === activeCarreraId) || carreras.find(c => c.facultad_id === activeFacultadId);
  const totalCreditosCarrera = carreraActiva?.creditos_totales || (activeFacultadId === 'psico' ? 320 : 360);
  const avancePorcentaje = Math.min(100, Math.round((creditosTotales / totalCreditosCarrera) * 100));

  const promGeneral = calcularPromedioGeneral(activeAsignaturas, evaluaciones, escalaVigente);
  const promAprobaciones = calcularPromedioAprobaciones(activeAsignaturas, evaluaciones, escalaVigente);
  const promPonderado = calcularPromedioPonderadoCreditos(activeAsignaturas, evaluaciones, escalaVigente);

  const frecuencias = calcularFrecuenciaConceptos(activeAsignaturas, evaluaciones, escalaVigente);
  const totalActividades = Object.values(frecuencias).reduce((a, b) => a + b, 0);

  const aprobadasCount = activeAsignaturas.filter(a => a.estado === 'aprobada').length;
  const totalCount = activeAsignaturas.length;

  const colorMap: Record<string, { bar: string; badge: string; text: string }> = {
    'Excelente':        { bar: 'bg-emerald-500', badge: 'bg-emerald-50 text-emerald-700 border-emerald-200', text: 'text-emerald-700' },
    'Muy Bueno':        { bar: 'bg-blue-500',    badge: 'bg-blue-50 text-blue-700 border-blue-200', text: 'text-blue-700' },
    'Bueno':            { bar: 'bg-indigo-500',  badge: 'bg-indigo-50 text-indigo-700 border-indigo-200', text: 'text-indigo-700' },
    'Aceptable':        { bar: 'bg-amber-500',   badge: 'bg-amber-50 text-amber-700 border-amber-200', text: 'text-amber-700' },
    'Insuficiente':     { bar: 'bg-rose-400',    badge: 'bg-rose-50 text-rose-700 border-rose-200', text: 'text-rose-700' },
    'Muy Insuficiente': { bar: 'bg-red-600',     badge: 'bg-red-100 text-red-800 border-red-300', text: 'text-red-800' },
  };

  if (isLoading) {
    return (
      <div className="w-full max-w-7xl mx-auto px-4 sm:px-6 py-5 sm:py-6 space-y-5 sm:space-y-6 animate-pulse">
        <div>
          <div className="h-8 w-72 bg-slate-200 rounded-lg" />
          <div className="h-4 w-60 bg-slate-100 rounded-md mt-2" />
        </div>
        <div className="bg-white rounded-2xl border border-slate-200/60 p-6 h-48 flex items-center gap-8 shadow-sm">
          <div className="w-36 h-36 rounded-full bg-slate-100 shrink-0" />
          <div className="flex-1 space-y-4">
            <div className="h-4 w-48 bg-slate-200 rounded" />
            <div className="h-6 w-64 bg-slate-200 rounded" />
            <div className="grid grid-cols-2 gap-4 pt-2 border-t border-slate-100">
              <div className="h-10 bg-slate-100 rounded-lg" />
              <div className="h-10 bg-slate-100 rounded-lg" />
            </div>
          </div>
        </div>
        <div className="bg-white rounded-2xl p-6 border border-slate-200/60 h-52 shadow-sm" />
      </div>
    );
  }

  return (
    <div className="w-full max-w-7xl mx-auto px-4 sm:px-6 py-5 sm:py-6 space-y-5 sm:space-y-6 animate-in fade-in duration-300">
      <div>
        <h1 className="text-3xl font-semibold tracking-tight text-slate-900">
          Escolaridad &amp; Resumen Académico
        </h1>
        <p className="text-sm font-medium text-slate-500 mt-1">
          Formato oficial Udelar - Escala Conceptual 2025
        </p>
      </div>

      {/* Hero Section - Avance de Carrera (Signature Element Radial / Donut) */}
      <div className="bg-white rounded-2xl border border-slate-200/60 p-6 shadow-[0_2px_8px_rgba(0,0,0,0.04)]">
        <div className="flex flex-col sm:flex-row items-center gap-8">
          {/* Anillo de Progreso Radial */}
          <div className="relative w-36 h-36 shrink-0 flex items-center justify-center p-1">
            {(() => {
              const size = 144;
              const strokeWidth = 10;
              const center = size / 2;
              const padding = 6;
              const radius = (size - strokeWidth) / 2 - padding;
              const circumference = 2 * Math.PI * radius;
              const offset = circumference - (avancePorcentaje / 100) * circumference;

              return (
                <svg
                  width={size}
                  height={size}
                  viewBox={`0 0 ${size} ${size}`}
                  className="w-full h-full"
                >
                  <g transform={`rotate(-90 ${center} ${center})`}>
                    {/* Pista de fondo (surface-container) */}
                    <circle
                      cx={center}
                      cy={center}
                      r={radius}
                      className="stroke-slate-100"
                      strokeWidth={strokeWidth}
                      fill="transparent"
                    />
                    {/* Trazo animado (primary) */}
                    <circle
                      cx={center}
                      cy={center}
                      r={radius}
                      className="stroke-primary transition-[stroke-dashoffset] duration-700 ease-out"
                      strokeWidth={strokeWidth}
                      strokeDasharray={circumference}
                      strokeDashoffset={offset}
                      strokeLinecap="round"
                      fill="transparent"
                    />
                  </g>
                </svg>
              );
            })()}
            {/* Texto Central Display */}
            <div className="absolute inset-0 flex flex-col items-center justify-center text-center select-none">
              <span className="text-3xl font-extrabold tracking-tight font-mono text-slate-900 leading-none">
                {avancePorcentaje}%
              </span>
              <span className="text-[10px] font-semibold text-slate-500 uppercase tracking-widest mt-1">
                Avance
              </span>
            </div>
          </div>          {/* Información Compacta de Métricas Secundarias */}
          <div className="flex-1 space-y-4 text-center sm:text-left">
            <div>
              <span className="text-xs font-bold text-slate-500 uppercase tracking-wider font-sans block">
                Progreso Global de Carrera
              </span>
              <div className="h-7 flex items-center justify-center sm:justify-start mt-0.5">
                {siglaFacultad ? (
                  <h2 className="text-lg font-bold text-slate-800 tracking-tight">
                    {carreraActiva?.nombre || `${siglaFacultad}: Plan Registrado`}
                  </h2>
                ) : (
                  <div className="h-5 w-48 bg-slate-200/80 rounded animate-pulse" />
                )}
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 pt-2 border-t border-slate-100">
              <div className="flex items-center gap-3">
                <div className="w-2.5 h-2.5 rounded-full bg-primary shrink-0" />
                <div>
                  <span className="text-[11px] font-medium text-slate-500 block leading-tight">
                    Créditos Acumulados
                  </span>
                  <div className="flex items-baseline gap-1 mt-0.5">
                    <span className="text-xl font-bold font-mono text-slate-900">{creditosTotales}</span>
                    <span className="text-xs text-slate-500 font-mono">/ {totalCreditosCarrera} CR</span>
                  </div>
                </div>
              </div>

              <div className="flex items-center gap-3">
                <div className="w-2.5 h-2.5 rounded-full bg-emerald-500 shrink-0" />
                <div>
                  <span className="text-[11px] font-medium text-slate-500 block leading-tight">
                    {materias} Aprobadas
                  </span>
                  <div className="flex items-baseline gap-1 mt-0.5">
                    <span className="text-xl font-bold font-mono text-emerald-600">{aprobadasCount}</span>
                    <span className="text-xs text-slate-500 font-mono">/ {totalCount} {materias.toLowerCase()}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Promedios Oficiales - Contenido Principal con elevacion y acentos */}
      <div className="bg-white rounded-2xl p-6 border border-slate-200/60 shadow-[0_2px_8px_rgba(0,0,0,0.05)] space-y-4">
        <div className="flex items-center justify-between border-b border-slate-100 pb-3">
          <div className="flex items-center gap-2">
            <Calculator className="w-5 h-5 text-primary-text" />
            <h2 className="text-base font-bold text-slate-900 tracking-tight">
              Promedios Oficiales de Escolaridad ({siglaFacultad})
            </h2>
          </div>
          <span className="text-xs font-mono text-slate-500 font-medium uppercase tracking-wider">
            Escala 0 - 10 equivalentes
          </span>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-5 pt-2">
          {/* Promedio General: Hero Metric Card (Nivel 1 Primario) */}
          <div className="md:col-span-2 bg-primary text-white p-6 rounded-2xl shadow-lg flex flex-col justify-between relative group">
            <div className="absolute inset-0 rounded-2xl overflow-hidden pointer-events-none">
              <div className="absolute -right-8 -bottom-8 w-40 h-40 bg-white/10 rounded-full blur-2xl group-hover:scale-125 transition-transform duration-500" />
            </div>
            
            <div className="relative z-10">
              <div className="flex items-center justify-between">
                <span className="text-xs font-bold uppercase tracking-wider text-white/80 font-sans">
                  Promedio General Oficial
                </span>
                <HelpIcon position="bottom-left" text="Suma de calificaciones de todas las materias y evaluaciones dividida entre la cantidad total de actividades (aprobadas y reprobadas)." />
              </div>
              <p className="text-xs text-white/70 mt-1">Todas las actividades académicas (aprobadas y reprobadas)</p>
            </div>

            {(() => {
              // Recolectar calificaciones de registros reprobados del historial académico (solo de asignaturas no eliminadas)
              const activeAsigIds = new Set(activeAsignaturas.map(a => a.id));
              const reprobados = historialAcademico.filter(h => h.resultado === 'reprobado' && activeAsigIds.has(h.asignatura_id));
              const hayReprobadosSinNota = reprobados.some(h => h.calificacion === null);
              const reprobadosConNota = reprobados.filter(h => h.calificacion !== null);
              const totalReprobados = reprobadosConNota.length;
              const ESCALA_10 = 10;
              const sumaReprobados = reprobadosConNota.reduce((acc, h) => acc + (h.calificacion as number), 0);
              let promedioFinal: number;
              if (totalReprobados > 0) {
                const actividades = activeAsignaturas.filter(a =>
                  a.estado === 'aprobada' ||
                  evaluaciones.some(e => e.asignatura_id === a.id && (e.nota !== undefined || e.porcentaje_logro !== undefined))
                );
                const countActividades = Math.max(actividades.length, 1);
                promedioFinal = (promGeneral * countActividades + sumaReprobados) / (countActividades + totalReprobados);
                promedioFinal = Math.min(ESCALA_10, Math.max(0, promedioFinal));
              } else {
                promedioFinal = promGeneral;
              }
              return (
                <div className="mt-6 flex items-baseline gap-2 relative z-10">
                  <span className="text-4xl md:text-5xl font-extrabold font-mono tracking-tight text-white">{promedioFinal.toFixed(2)}</span>
                  <span className="text-sm text-white/80 font-mono font-medium">/ 10.00</span>
                  {hayReprobadosSinNota && (
                    <span
                      title="Promedio calculado mediante interpolación según la escala conceptual oficial de Udelar (no es un dato oficial verificado por Bedelía)"
                      className="ml-2 cursor-help text-white text-[11px] font-semibold font-mono select-none bg-white/20 px-2.5 py-0.5 rounded-full border border-white/30 backdrop-blur-xs"
                      aria-label="Promedio aproximado"
                    >
                      ≈ Aproximado*
                    </span>
                  )}
                </div>
              );
            })()}
          </div>

          {/* Columna con Métricas Secundarias Compactas (Nivel 2) */}
          <div className="flex flex-col gap-3 justify-between">
            {/* Promedio Aprobaciones */}
            <div className="bg-white p-4 rounded-xl border border-slate-200/60 shadow-2xs hover:border-emerald-300/60 transition-all flex items-center justify-between">
              <div>
                <div className="flex items-center gap-1.5">
                  <span className="text-xs font-semibold text-slate-700 font-sans">Aprobaciones</span>
                  <HelpIcon text="Promedio simple calculado únicamente considerando las asignaturas y evaluaciones que resultaron aprobadas." />
                </div>
                <p className="text-[10px] text-slate-500">Solo materias/evals aprobadas</p>
              </div>
              <div className="text-right">
                <span className="text-xl font-bold font-mono text-emerald-700">{promAprobaciones.toFixed(2)}</span>
                <span className="text-[10px] text-slate-500 font-mono block">/ 10</span>
              </div>
            </div>

            {/* Promedio Ponderado por Créditos */}
            <div className="bg-white p-4 rounded-xl border border-slate-200/60 shadow-2xs hover:border-primary/40 transition-all flex items-center justify-between">
              <div>
                <div className="flex items-center gap-1.5">
                  <span className="text-xs font-semibold text-slate-700 font-sans">Ponderado (CR)</span>
                  <HelpIcon text="Promedio ponderado por la cantidad de créditos de cada materia aprobada. Las materias con más créditos pesan proporcionalmente más." />
                </div>
                <p className="text-[10px] text-slate-500">Pesado por créditos académicos</p>
              </div>
              <div className="text-right">
                <span className="text-xl font-bold font-mono text-primary-text">{promPonderado.toFixed(2)}</span>
                <span className="text-[10px] text-slate-500 font-mono block">/ 10</span>
              </div>
          </div>
        </div>
        </div>
      </div>

      {/* Grafico de Frecuencia */}
      <div className="bg-white rounded-2xl p-6 border border-slate-200/60 shadow-[0_2px_8px_rgba(0,0,0,0.05)] space-y-6">
        <div className="flex items-center justify-between border-b border-slate-100 pb-3">
          <div className="flex items-center gap-2">
            <BarChart3 className="w-5 h-5 text-primary-text" />
            <h2 className="text-base font-bold text-slate-900 tracking-tight">
              Frecuencia por Concepto (6 Niveles Udelar)
            </h2>
          </div>
          <span className="text-xs font-mono text-slate-500">
            Total actividades: <strong className="text-slate-800">{totalActividades}</strong>
          </span>
        </div>

        {totalActividades === 0 ? (
          <div className="flex flex-col items-center justify-center py-12 gap-3">
            <div className="w-14 h-14 rounded-full bg-slate-50 border border-slate-200/60 flex items-center justify-center">
              <BarChart3 size={24} className="text-slate-300" />
            </div>
            <p className="text-sm font-medium text-slate-600">Sin evaluaciones registradas aún.</p>
            <p className="text-xs text-slate-500">Agregá evaluaciones a tus asignaturas para ver las estadísticas.</p>
          </div>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4 pt-2">
            {escalaVigente.niveles.map((nivel: NivelCalificacion) => {
              const count = frecuencias[nivel.concepto] || 0;
              const porcentajeBarra = totalActividades > 0 ? (count / totalActividades) * 100 : 0;
              const styles = colorMap[nivel.concepto] || { bar: 'bg-slate-400', badge: 'bg-slate-100 text-slate-700', text: 'text-slate-700' };
              return (
                <div key={nivel.concepto} className="flex flex-col items-center justify-between bg-white p-4 rounded-xl border border-slate-200/60 hover:-translate-y-0.5 hover:shadow-[0_8px_24px_rgb(0,0,0,0.07)] transition-all duration-300">
                  <div className="w-full text-center mb-3">
                    <span className={`inline-block px-2 py-0.5 text-[11px] font-bold rounded-md border ${styles.badge}`}>{nivel.concepto}</span>
                    <p className="text-[10px] text-slate-500 font-mono mt-1">&#8805; {nivel.porcentaje_min}%</p>
                  </div>
                  <div className="w-full h-32 flex items-end justify-center bg-slate-50 rounded-lg p-1 relative overflow-hidden border border-slate-100">
                    <div className={`w-full rounded-md ${styles.bar} transition-all duration-700 ease-out`} style={{ height: count > 0 ? `${Math.max(15, porcentajeBarra)}%` : '4px' }} />
                    {count > 0 && (
                      <span className="absolute top-2 text-xs font-mono font-bold text-slate-700 bg-white/90 px-1.5 py-0.5 rounded shadow-xs border border-slate-100">{count}</span>
                    )}
                  </div>
                  <div className="mt-3 text-center">
                    <span className={`text-sm font-bold font-mono ${styles.text}`}>{count}</span>
                    <span className="text-[10px] text-slate-500 block font-mono">({totalActividades > 0 ? ((count / totalActividades) * 100).toFixed(0) : 0}%)</span>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Áreas de Conocimiento y Módulos de Carrera */}
      {(() => {
        const facultadModulos = modulos.filter(m => m.facultad_id === activeFacultadId);
        if (facultadModulos.length === 0) return null;

        return (
          <div className="bg-white rounded-2xl p-6 border border-slate-200/60 shadow-[0_2px_8px_rgba(0,0,0,0.05)] space-y-4">
            <div className="flex items-center justify-between border-b border-slate-100 pb-3">
              <div className="flex items-center gap-2">
                <BarChart3 className="w-5 h-5 text-primary-text" />
                <h2 className="text-base font-bold text-slate-900 tracking-tight">
                  Créditos Mínimos por Área de Conocimiento / Módulo
                </h2>
              </div>
              <span className="text-xs font-mono text-slate-500 font-medium">
                Requisitos oficiales de plan de estudios
              </span>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 pt-2">
              {facultadModulos.map(m => {
                const creditosObtenidos = calcularCreditosPorModulo(activeAsignaturas, acreditacionesLibres, m.id);
                const pct = Math.min(100, Math.round((creditosObtenidos / m.creditos_minimos) * 100));
                const esCompleto = creditosObtenidos >= m.creditos_minimos;

                return (
                  <div key={m.id} className="bg-slate-50/70 p-4 rounded-xl border border-slate-200/60 hover:bg-white hover:shadow-sm transition-all space-y-2">
                    <div className="flex items-start justify-between gap-2">
                      <span className="text-xs font-bold text-slate-800 leading-tight">
                        {m.nombre}
                      </span>
                      {esCompleto ? (
                        <span className="text-[10px] font-bold font-mono bg-emerald-50 text-emerald-700 border border-emerald-200 px-2 py-0.5 rounded-full shrink-0">
                          ✓ Cumplido
                        </span>
                      ) : (
                        <span className="text-[10px] font-bold font-mono text-slate-600 bg-white border border-slate-200 px-2 py-0.5 rounded-full shrink-0">
                          {pct}%
                        </span>
                      )}
                    </div>

                    <div className="space-y-1">
                      <div className="flex justify-between items-baseline text-[11px] font-mono">
                        <span className="text-slate-500">Progreso:</span>
                        <span className="font-semibold text-slate-900">
                          {creditosObtenidos} <span className="text-slate-400 font-normal">/ {m.creditos_minimos} CR</span>
                        </span>
                      </div>

                      <div className="w-full h-2 bg-slate-200/70 rounded-full overflow-hidden">
                        <div
                          className={`h-full rounded-full transition-all duration-500 ${esCompleto ? 'bg-emerald-500' : 'bg-primary'}`}
                          style={{ width: `${pct}%` }}
                        />
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        );
      })()}
    </div>
  );
}